/**
 * QUOTATION RELATION CARD — card personalizzata per RelationPanel quando il
 * targetType è Quotation (stile mockup): emblema documento + titolo + nome del
 * file (se presente) + importo (se ≠ 0) + chip di stato.
 *
 * Fa due fetch propri al mount: il preventivo (titolo/importo/stato) e i suoi
 * file (per il nome del primo documento). La label risolta dal registry contiene
 * solo il titolo, quindi importo/stato/file si leggono qui.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@crm/shared';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { quotationApi } from '../entities/quotation/quotationApi';
import { quotationFileApi } from '../entities/quotationFile/quotationFileApi';
import type { Quotation } from '../entities/quotation/quotationMappers';
import { QuotationsGlyph } from './icons';
import { quotationStatusChipClass } from '../entities/quotation/quotationTypes';
import type { RelationItemContext } from './RelationPanel';

interface QuotationRelationCardProps {
  ctx: RelationItemContext;
  /** quotationId = ctx.relationship.classPK2 (lato opposto alla source). */
  quotationId: number;
}

export function QuotationRelationCard({ ctx, quotationId }: QuotationRelationCardProps) {
  const { t } = useTranslation();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    quotationApi
      .getById(quotationId)
      .then((q) => { if (!cancelled) setQuotation(q); })
      .catch(() => { if (!cancelled) setQuotation(null); });
    // Nome del primo file collegato (best-effort: se non ci sono file resta null).
    quotationFileApi
      .getByQuotation(quotationId)
      .then((page) => { if (!cancelled) setFileName(page.items[0]?.fileName ?? null); })
      .catch(() => { /* nessun file / errore: si mostra solo il resto. */ });
    return () => { cancelled = true; };
  }, [quotationId]);

  const name = quotation?.title ?? ctx.label;
  const amount = quotation?.amount ?? 0;
  const status = quotation?.status;

  return (
    <div className="relation-card">
      <span className="rel-emblem doc"><QuotationsGlyph /></span>
      <div className="relation-card__main">
        <Link to={ctx.path} className="relation-link relation-card__title">{name}</Link>
        {fileName && <div className="relation-card__file">{fileName}</div>}
      </div>
      {(amount !== 0 || status) && (
        <div className="relation-card__right">
          {amount !== 0 && <span className="relation-card__amount">{formatCurrency(amount)}</span>}
          {status && (
            <span className={`chip ${quotationStatusChipClass(status)} xs`}>
              {t(`quotation.statuses.${status}`)}
            </span>
          )}
        </div>
      )}
      <IconButton
        size="small"
        onClick={ctx.requestDelete}
        aria-label={t('relationship.removeLabel')}
        className="relation-card__remove"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
}
